import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';

import { MasterDocument } from '@util/firestore/master';
import { ProductDocument } from '@util/types/firestore/products';
import { ListingDisplay } from '@c/MasterProductsPageV2';
import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useEffectivePrice } from 'context/StoreSaleContext';
import { formatCurrency, getCartItemFromProductDocument } from '@util/index';
import { AffirmTag } from '@c/affirm/AffirmTag';
import { getProductUrl } from '@util/urlHelpers';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';
import SafeImage from '@ui/SafeImage';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';

const HeartIconButton = dynamic(() => import('@c/icons/buttons/HeartButton'), {
  ssr: false,
});

interface FeaturedListingProps {
  master: MasterDocument;
  results: ListingDisplay[];
}

const GOOD_OR_BETTER_CONDITIONS = new Set([
  'Good',
  'Excellent',
  'Like New',
  'Manufacturer Refurbished or Unopened',
  'Open Box',
]);

function selectFeaturedListing(results: ListingDisplay[]): ListingDisplay | null {
  if (!results || results.length === 0) return null;

  const goodOrBetter = results.filter(
    (p) => !p.out_of_stock && p.condition && GOOD_OR_BETTER_CONDITIONS.has(p.condition)
  );

  const pool = goodOrBetter.length > 0 ? goodOrBetter : results.filter((p) => !p.out_of_stock);

  if (pool.length === 0) return null;

  return pool.reduce((lowest, current) => {
    return current.price < lowest.price ? current : lowest;
  }, pool[0]);
}

export default function FeaturedListing({ master, results }: FeaturedListingProps) {
  const product = selectFeaturedListing(results);
  const storePrice = useEffectivePrice(product as ProductDocument | null);

  const { userDoc, user } = useAuth();
  const { increaseCartQty, addGuestCartItem } = useShoppingCart();

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  if (!product) return null;

  const imageSrcs =
    product.images && product.images.length > 0
      ? product.images.map((img) => img.full)
      : product.thumbnail
      ? [product.thumbnail]
      : [];

  // Disable while an add is in flight so rapid taps can't queue duplicate
  // writes (GB #1323). increaseCartQty / addGuestCartItem own the stock cap,
  // toasts, and opening the cart.
  const handleAddToCart = async () => {
    if (isAdding) return;
    const cartItem = getCartItemFromProductDocument(product as ProductDocument, storePrice ?? undefined);
    if (!cartItem) return;
    setIsAdding(true);
    try {
      if (userDoc) {
        await increaseCartQty(cartItem);
      } else if (!user) {
        addGuestCartItem(cartItem, (product as ProductDocument).stock);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const isOnSale = product.on_sale && product.og_price > product.price;
  const displayPrice = storePrice?.applied ? storePrice.unitCents / 100 : product.price;

  return (
    <section
      id="buy"
      aria-label={`Buy ${master.brand} ${master.model}`}
      className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
    >
      <Link
        href={getProductUrl(product.slug)}
        className="block bg-white rounded-[2rem] shadow-[0_4px_24px_rgba(0,0,0,0.12)] p-[2rem] md:p-[3rem] hover:shadow-[0_6px_30px_rgba(0,0,0,0.16)] transition-shadow"
      >
        {/* Mobile (#1453 §3.2): reorder the stacked columns so price + Add to
            Cart (RIGHT) sit first, above the image and highlights, keeping the
            purchase signal in the first phone screen. md:order-none restores
            natural source order (image, center, right) at md+ — so the
            768-1023px stacked band and the lg+ 3-col grid both match
            origin/main (desktop and tablet pixel-unchanged); only <768px is
            reordered. */}
        <div className="grid grid-cols-1 lg:grid-cols-[calc(40%-2rem)_calc(35%-2rem)_calc(25%-2rem)] gap-[2rem] md:gap-[3rem]">

          {/* LEFT: Image — fills height, crop OK */}
          <div className="order-2 md:order-none">
            <div className="relative h-[24rem] sm:h-[28rem] rounded-[0.8rem] overflow-hidden bg-gray-50">
              {imageSrcs.length > 0 ? (
                <>
                  <Swiper
                    modules={[Navigation]}
                    onSwiper={(swiper: SwiperType) => {
                      swiperRef.current = swiper;
                      setIsBeginning(swiper.isBeginning);
                      setIsEnd(swiper.isEnd);
                    }}
                    onSlideChange={(swiper: SwiperType) => {
                      setIsBeginning(swiper.isBeginning);
                      setIsEnd(swiper.isEnd);
                    }}
                    className="h-full w-full"
                  >
                    {imageSrcs.map((src, index) => (
                      <SwiperSlide key={index}>
                        <div className="h-full w-full">
                          <SafeImage
                            src={src}
                            alt={`${master.brand} ${master.model} — image ${index + 1}`}
                            width={700}
                            height={700}
                            className="object-cover w-full h-full"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Heart — top-right of image, matching ProductCard */}
                  <div className="absolute right-4 top-4 z-20 cursor-pointer" onClick={(e) => e.preventDefault()}>
                    <HeartIconButton itemId={product.id} hideCount />
                  </div>

                  {!isBeginning && (
                    <button
                      onClick={(e) => { e.preventDefault(); swiperRef.current?.slidePrev(); }}
                      aria-label="Previous image"
                      className="absolute left-[1rem] top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-[3.6rem] h-[3.6rem] rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors shadow-md"
                    >
                      <HiArrowLeft size={18} />
                    </button>
                  )}

                  {!isEnd && imageSrcs.length > 1 && (
                    <button
                      onClick={(e) => { e.preventDefault(); swiperRef.current?.slideNext(); }}
                      aria-label="Next image"
                      className="absolute right-[1rem] top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-[3.6rem] h-[3.6rem] rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors shadow-md"
                    >
                      <HiArrowRight size={18} />
                    </button>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-[1.4rem]">
                  No image available
                </div>
              )}
            </div>
          </div>

          {/* CENTER: Title + Condition + Highlights + Sell yours */}
          <div className="order-3 md:order-none flex flex-col">
            {/* Title */}
            <h3 className="text-[2rem] font-bold text-black leading-snug">
              {product.title}
            </h3>

            {/* Condition — tight to title */}
            {product.condition && (
              <p className="text-[1.2rem] text-gray-600 mt-[0.4rem]">
                Condition: <span className="text-brand-primary font-medium">{getConditionShortLabel(product.condition)}</span>
              </p>
            )}

            {/* Product Highlights — centered in remaining space */}
            {master.featuresShort && master.featuresShort.length > 0 && (
              <div className="my-auto py-[1.6rem]">
                <h4 className="font-bold text-[1.5rem] mb-[0.5rem]">Product Highlights</h4>
                <ul className="list-disc pl-[2rem] space-y-[0.3rem]">
                  {master.featuresShort.slice(0, 4).map((feature, index) => (
                    <li key={index} className="text-[1.3rem] text-gray-600">
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sell yours — pinned to bottom */}
            <p className="text-[1.3rem] text-gray-500 mt-auto">
              Sell your {master.brand} {master.model} on GearFocus
            </p>
          </div>

          {/* RIGHT: Price + Shipping at top, Affirm + Cart + Protection at bottom */}
          <div className="order-1 md:order-none flex flex-col items-end text-right min-h-full">
            {/* Price */}
            <p className="text-[3.2rem] font-extrabold text-black leading-none">
              {formatCurrency(displayPrice)}
            </p>

            {isOnSale && (
              <p className="text-[1.4rem] text-gray-400 line-through mt-[0.4rem]">
                {formatCurrency(product.og_price)}
              </p>
            )}

            {storePrice?.applied && (
              <p className="text-[1.4rem] text-gray-500">
                <span className="line-through">{formatCurrency(product.price)}</span>{' '}
                <span>{storePrice.applied.percentOff}% off store sale</span>
              </p>
            )}
            {/* Shipping — right below price */}
            <div className="mt-[0.4rem]">
              {product.shipping_costs && product.shipping_costs.length > 0 ? (
                product.shipping_costs[0].cost === 0 ? (
                  <p className="text-[1.3rem] text-green-600 font-medium">🚚 Free Shipping</p>
                ) : (
                  <p className="text-[1.3rem] text-gray-500">
                    + {formatCurrency(product.shipping_costs[0].cost)} Shipping
                  </p>
                )
              ) : (
                <p className="text-[1.3rem] text-gray-500">Shipping at Checkout</p>
              )}
            </div>

            {/* Spacer — pushes Affirm/Cart/Protection to bottom */}
            <div className="mt-auto w-full flex flex-col items-end gap-[0.8rem]">
              {/*
                Affirm — static eligibility-gated tag, no invented number.
                selectFeaturedListing() above already excludes out_of_stock
                candidates from the pool, so `product` can't be out_of_stock
                here — the out_of_stock half of this guard is
                defense-in-depth against that invariant changing later
                without this call site being revisited.

                is_auction is NOT filtered by selectFeaturedListing (an
                auction CAN be selected as the featured listing), so this
                guard is the only thing stopping financing messaging on a
                listing whose "price" is a starting bid, not a buy-it-now
                amount — same reasoning as PDP's Affirm gate and
                ListProductCard's grid-card gate (GB #1372 QA follow-up).

                No buyerCountry here either (fresh-eyes review on PR #1451),
                same reasoning as ListProductCard: /m/ pages are a
                guest-heavy surface and AffirmTag has no country prop at
                all (it never mounts the Stripe element a country mismatch
                would need to suppress). Kept on AffirmTag's existing
                US-assumption default.
              */}
              {!product.out_of_stock && !product.is_auction && (
                <AffirmTag
                  amountUSD={displayPrice}
                  className="whitespace-nowrap justify-end"
                />
              )}

              {/* Add to Cart */}
              <button
                onClick={(e) => { e.preventDefault(); handleAddToCart(); }}
                disabled={product.out_of_stock || isAdding}
                className="w-full bg-brand-primary text-white font-bold uppercase rounded-[1rem] py-[1.2rem] text-[1.6rem] tracking-wide hover:bg-brand-secondary disabled:opacity-50 transition-colors"
              >
                {product.out_of_stock ? 'Out of Stock' : isAdding ? 'Adding…' : 'ADD TO CART'}
              </button>

              {/* Buyer Protection */}
              <p className="text-[1.1rem] text-gray-500 text-center w-full">
                Shop with confidence.{' '}
                <span className="text-[#1C5CD3] underline">
                  GearFocus Buyer Protection
                </span>
                {' '}is included with every purchase.
              </p>
            </div>
          </div>

        </div>
      </Link>
    </section>
  );
}
